import { Drawer as AntdDrawer, Divider, DrawerProps } from 'antd';
import { useContext } from 'react';
import { useLocation } from 'react-router';

import { GovUaLogo, OrderServiceLink } from '@/features/ui';

import { Button, ButtonVariant, ConditionView, Icon, Link, LinkAsButton, Typography } from '@/shared/ui';

import { useNavigation } from '@/config/navigation';

import { ROUTES } from '@/router/config';

import AuthContext from '@/contexts/Auth/AuthContext';

import { AccessibilityPanelButton } from '../../AccessibilityPanel/ui/AccessibilityPanelButton';
import { MVSButton } from '../../Header/ui/MVSButton';
import { NavTabsMapping } from '../../Header/ui/uk.l10n';

import './DrawerStyles.scss';

interface Props {
  isDrawerOpen: DrawerProps['open'];
  toggleDrawer: () => void;
  logoutHandler: () => void;
  placement?: DrawerProps['placement'];
}

export const Drawer: React.FC<Props> = ({ isDrawerOpen, toggleDrawer, logoutHandler, placement = 'left' }) => {
  const authContext = useContext(AuthContext);
  const { pathname } = useLocation();
  const navigation = useNavigation();

  const handleScroll = (open: boolean): void => {
    const body = document.querySelector('body');

    if (!body) return;

    body.style.overflow = open ? 'hidden' : 'auto';
  };

  return (
    <AntdDrawer
      open={isDrawerOpen}
      onClose={toggleDrawer}
      placement={placement}
      closable={false}
      afterOpenChange={handleScroll}
    >
      <div className="drawer">
        <div className="drawer__header">
          <Typography as="p" variant="H1">
            Меню
          </Typography>
          <Button
            className="drawer__close-btn"
            aria-label="Close menu"
            variant={ButtonVariant.ICON_GHOST}
            onClick={toggleDrawer}
          >
            <Icon name="close" />
          </Button>
        </div>

        <Divider className="drawer__divider" />

        <ConditionView if={!!navigation?.length}>
          <div className="drawer__wrapper">
            {navigation.map((headerTab: string) => (
              <Link to={headerTab} className="drawer__nav-item" key={headerTab} onClick={toggleDrawer}>
                <Typography as="span">{NavTabsMapping[headerTab]}</Typography>
              </Link>
            ))}
          </div>

          <Divider className="drawer__divider" />
        </ConditionView>

        <div className="drawer__wrapper">
          <AccessibilityPanelButton onClick={toggleDrawer} color="blue" />

          {!authContext.isLoggedIn && (
            <LinkAsButton
              to={ROUTES.LOGIN}
              variant={ButtonVariant.ICON_GHOST}
              state={{
                nextPage: pathname,
              }}
              className="drawer__auth-btn"
              onClick={toggleDrawer}
            >
              <Icon name="account" width={24} height={24} />
              <Typography as="span">Увійти</Typography>
            </LinkAsButton>
          )}

          {authContext.isLoggedIn && (
            <>
              <LinkAsButton
                to={ROUTES.USER.PROFILE}
                variant={ButtonVariant.ICON_GHOST}
                className="drawer__auth-btn"
                onClick={toggleDrawer}
              >
                <Icon name="account" width={24} height={24} />
                <Typography className="drawer__auth-btn-text" as="span">
                  Особистий кабінет
                </Typography>
              </LinkAsButton>

              <LinkAsButton
                to={ROUTES.LOGIN}
                state={{
                  nextPage: pathname,
                }}
                variant={ButtonVariant.ICON_GHOST}
                className="drawer__auth-btn"
                onClick={logoutHandler}
              >
                <Icon name="exit" width={24} height={24} />
                <Typography as="span">Вийти</Typography>
              </LinkAsButton>
            </>
          )}
        </div>

        <div className="drawer__footer">
          <div className="drawer__footer-inner">
            <div className="drawer__footer-item">
              <OrderServiceLink shorten isResponsive onClick={toggleDrawer} />
            </div>

            <Divider className="drawer__divider" />

            <div className="drawer__footer-item">
              <GovUaLogo onClick={toggleDrawer} color="black" />
              <Divider className="drawer__divider" type="vertical" />
              <MVSButton color="black" />
            </div>
          </div>
        </div>
      </div>
    </AntdDrawer>
  );
};
